/** Ingresos (1.0 admin Revenue): rango de fechas → `admin-revenueReport` (solo lecturas). */
import { useEffect, useState, type ReactElement } from 'react';
import { api } from '@nuvvu/shared/api';
import type { AdminRevenueReportOutput } from '@nuvvu/shared/api';
import { call, errorText } from '../api.js';
import { btn, card, field, money, muted, Notice, Table, td } from '../lib/ui.js';

const day = (d: Date): string => d.toISOString().slice(0, 10);
const fmt = (amount: number, currency: string): string => money({ amount, currency });

export function RevenuePage(): ReactElement {
  const [from, setFrom] = useState(day(new Date(Date.now() - 30 * 86_400_000)));
  const [to, setTo] = useState(day(new Date()));
  const [data, setData] = useState<AdminRevenueReportOutput | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const run = (): void => {
    setBusy(true);
    setError(null);
    call(api.admin.revenueReport, {
      from: new Date(from).getTime(),
      to: new Date(to).getTime() + 86_400_000,
    })
      .then(setData)
      .catch((e: unknown) => {
        setError(errorText(e));
      })
      .finally(() => {
        setBusy(false);
      });
  };
  useEffect(run, []);
  return (
    <>
      <h1 style={{ margin: 0 }}>Ingresos</h1>
      <p style={muted}>
        Pedidos completados por fecha de cierre; lo que cobra nuvvu = protección + recargos −
        subsidios de campañas de envío. Retiradas instantáneas: tarifa cobrada. Importes en céntimos
        convertidos.
      </p>
      <form
        style={{ ...card, display: 'flex', gap: 8, alignItems: 'end', flexWrap: 'wrap' }}
        onSubmit={(e) => {
          e.preventDefault();
          run();
        }}
      >
        <label style={{ display: 'grid', gap: 4, fontSize: 13 }}>
          Desde
          <input
            style={field}
            type="date"
            value={from}
            onChange={(e) => {
              setFrom(e.target.value);
            }}
          />
        </label>
        <label style={{ display: 'grid', gap: 4, fontSize: 13 }}>
          Hasta (incluido)
          <input
            style={field}
            type="date"
            value={to}
            onChange={(e) => {
              setTo(e.target.value);
            }}
          />
        </label>
        <button type="submit" style={btn(true)} disabled={busy}>
          Calcular
        </button>
      </form>
      <Notice text={error} error />
      {data && (
        <>
          <h2 style={{ margin: 0, fontSize: 16 }}>Pedidos completados</h2>
          <Table
            head={[
              'Moneda',
              'Pedidos',
              'GMV',
              'Protección',
              'Recargos',
              'Subsidios envío',
              'Neto nuvvu',
            ]}
          >
            {data.by_currency.map((r) => (
              <tr key={r.currency}>
                <td style={td}>{r.currency}</td>
                <td style={td}>{r.orders}</td>
                <td style={td}>{fmt(r.gmv, r.currency)}</td>
                <td style={td}>{fmt(r.protection_fees, r.currency)}</td>
                <td style={td}>{fmt(r.surcharges, r.currency)}</td>
                <td style={td}>−{fmt(r.campaign_subsidies, r.currency)}</td>
                <td style={td}>
                  <strong>
                    {fmt(r.protection_fees + r.surcharges - r.campaign_subsidies, r.currency)}
                  </strong>
                </td>
              </tr>
            ))}
            {data.by_currency.length === 0 && (
              <tr>
                <td style={td} colSpan={7}>
                  Sin pedidos completados en el rango.
                </td>
              </tr>
            )}
          </Table>
          <h2 style={{ margin: 0, fontSize: 16 }}>Por origen del pago</h2>
          <p style={{ ...muted, margin: 0 }}>
            Siempre salen las cuatro vías: App Store queda a 0 hasta que Apple apruebe la app.
          </p>
          <Table head={['Origen', 'Activaciones (rango)', 'PRO activos (ahora)']}>
            {SOURCES.map(([key, label]) => (
              <tr key={key}>
                <td style={td}>{label}</td>
                <td style={td}>
                  {data.monetization
                    .filter((r) => sourceGroup(r.source) === key)
                    .reduce((n, r) => n + r.count, 0)}
                </td>
                <td style={td}>
                  {data.subscriptions
                    .filter((r) => sourceGroup(r.source) === key)
                    .reduce((n, r) => n + r.count, 0)}
                </td>
              </tr>
            ))}
          </Table>
          <h2 style={{ margin: 0, fontSize: 16 }}>
            Monetización activada (destacados, subidas, escaparates)
          </h2>
          <Table head={['Tipo', 'Origen', 'Activaciones']}>
            {data.monetization.map((r) => (
              <tr key={`${r.kind}|${r.source}`}>
                <td style={td}>{r.kind}</td>
                <td style={td}>{sourceLabel(r.source)}</td>
                <td style={td}>{r.count}</td>
              </tr>
            ))}
          </Table>
          <h2 style={{ margin: 0, fontSize: 16 }}>Suscripciones PRO activas (ahora)</h2>
          <Table head={['Nivel', 'Origen', 'Cuentas']}>
            {data.subscriptions.map((r) => (
              <tr key={`${r.tier}|${r.source}`}>
                <td style={td}>{r.tier}</td>
                <td style={td}>{sourceLabel(r.source)}</td>
                <td style={td}>{r.count}</td>
              </tr>
            ))}
          </Table>
          <h2 style={{ margin: 0, fontSize: 16 }}>Retiradas</h2>
          <Table head={['Estado', 'Retiradas', 'Tarifas instantáneas']}>
            {data.withdrawals.map((r) => (
              <tr key={r.status}>
                <td style={td}>{r.status}</td>
                <td style={td}>{r.count}</td>
                <td style={td}>{r.instant_fees ? money(r.instant_fees) : '–'}</td>
              </tr>
            ))}
          </Table>
        </>
      )}
    </>
  );
}

/** Vías de pago de la monetización y el PRO (27/10: App Store visible aunque esté a 0). */
const SOURCES: [string, string][] = [
  ['stripe', 'Web (tarjeta / Stripe)'],
  ['wallet', 'Saldo (wallet)'],
  ['play_store', 'Google Play'],
  ['app_store', 'App Store (Apple)'],
  ['other', 'Incluido / otros'],
];
function sourceGroup(src: string): string {
  if (src === 'stripe' || src === 'card') return 'stripe';
  if (src === 'wallet' || src === 'play_store' || src === 'app_store') return src;
  return 'other';
}
function sourceLabel(src: string): string {
  const named: Record<string, string> = {
    stripe: 'Stripe',
    card: 'Tarjeta (Stripe)',
    wallet: 'Saldo',
    play_store: 'Google Play',
    app_store: 'App Store',
    pro_included: 'Incluido en PRO',
    pro_weekly: 'PRO semanal',
  };
  return named[src] ?? (src || '—');
}
